/**
 * Bot players.
 *
 * Bots see exactly what a human in their seat sees (a PlayerView) — no peeking.
 * Decisions use Perfect-Information Monte Carlo: sample plausible deals of the
 * unseen cards that are consistent with everything observed (including which
 * players have shown out of a suit), play each candidate move out to the end of
 * the hand with a fast rule-of-thumb policy, and pick the move with the best
 * average score for the bot's team.
 */

import {
  type Card,
  type PlayedCard,
  type Seat,
  type Suit,
  type Team,
  SUITS,
  cardPower,
  currentWinner,
  effectiveSuit,
  isRightBower,
  isTrump,
  leftOf,
  legalPlays,
  newDeck,
  partnerOf,
  printedSuitOf,
  rankOf,
  teamOf,
} from './cards.js';
import { type GameAction, lowCards } from './game.js';
import { Rng } from './rng.js';
import type { PlayerView } from './view.js';

export interface BotOptions {
  /** Sampled deals per candidate decision. Higher = stronger and slower. */
  samples?: number;
  /** Seed for the bot's private randomness. */
  seed?: string | number;
  /** Expected-points margin required to call trump (tuning knob). */
  orderThreshold?: number;
  /** Extra expected points required before going alone (tuning knob). */
  aloneMargin?: number;
}

const DEFAULT_SAMPLES = 120;

// ---------------------------------------------------------------------------
// Fast simulation of the rest of a hand
// ---------------------------------------------------------------------------

interface Sim {
  hands: Card[][];
  trump: Suit;
  dealer: Seat;
  maker: Seat;
  makerAlone: boolean;
  defenderAlone: Seat | null;
  sittingOut: Seat[];
  currentTrick: PlayedCard[];
  leader: Seat;
  turn: Seat;
  tricksWon: [number, number];
  tricksPlayed: number;
}

function nextActive(sim: Sim, from: Seat): Seat {
  let s = leftOf(from);
  while (sim.sittingOut.includes(s)) s = leftOf(s);
  return s;
}

function activeCount(sim: Sim): number {
  return 4 - sim.sittingOut.length;
}

function lowest(cards: readonly Card[], trump: Suit | null): Card {
  return [...cards].sort((a, b) => cardPower(a, trump) - cardPower(b, trump))[0]!;
}

function highest(cards: readonly Card[], trump: Suit | null): Card {
  return [...cards].sort((a, b) => cardPower(b, trump) - cardPower(a, trump))[0]!;
}

/** Lowest card to throw away: prefer non-trump, and prefer emptying short suits. */
function throwOff(cards: readonly Card[], hand: readonly Card[], trump: Suit): Card {
  const nonTrump = cards.filter((c) => !isTrump(c, trump));
  if (nonTrump.length === 0) return lowest(cards, trump);
  const nonAces = nonTrump.filter((c) => rankOf(c) !== 'A');
  const pool = nonAces.length ? nonAces : nonTrump;
  const suitLen = (c: Card) => hand.filter((x) => effectiveSuit(x, trump) === effectiveSuit(c, trump)).length;
  return [...pool].sort((a, b) => suitLen(a) - suitLen(b) || cardPower(a, trump) - cardPower(b, trump))[0]!;
}

/** Rule-of-thumb play policy. Uses only the player's own hand and the table. */
export function heuristicPlay(
  hand: readonly Card[],
  trick: readonly PlayedCard[],
  trump: Suit,
  seat: Seat,
  makerTeam: Team,
  partnerActive: boolean,
  playersInTrick: number,
): Card {
  const legal = legalPlays(hand, trick, trump);
  if (legal.length === 1) return legal[0]!;
  const onMakerTeam = teamOf(seat) === makerTeam;

  if (trick.length === 0) {
    const trumps = legal.filter((c) => isTrump(c, trump));
    const plain = legal.filter((c) => !isTrump(c, trump));
    if (onMakerTeam && trumps.length > 0) {
      const right = trumps.find((c) => isRightBower(c, trump));
      if (right) return right;
      if (trumps.length >= 2) return highest(trumps, trump);
    }
    const aces = plain.filter((c) => rankOf(c) === 'A');
    if (aces.length) return aces[0]!;
    if (!onMakerTeam && plain.length) {
      // Defenders: lead from a short suit to set up trumping later.
      return throwOff(plain, hand, trump);
    }
    if (plain.length) return lowest(plain, trump);
    return onMakerTeam ? highest(trumps, trump) : lowest(trumps, trump);
  }

  const winner = currentWinner(trick, trump)!;
  const partnerWinning = partnerActive && winner === partnerOf(seat);
  const isLast = trick.length === playersInTrick - 1;
  const winners = legal.filter((c) => currentWinner([...trick, { seat, card: c }], trump) === seat);

  if (partnerWinning) {
    const winningCard = trick.find((p) => p.seat === winner)!.card;
    const led = effectiveSuit(trick[0]!.card, trump);
    const strong =
      isTrump(winningCard, trump)
        ? cardPower(winningCard, trump) >= 105
        : rankOf(winningCard) === 'A' && led !== trump;
    if (isLast || strong || winners.length === 0) return throwOff(legal, hand, trump);
    // Partner is winning weakly and someone still to play: overtake cheaply only if we must.
    return throwOff(legal, hand, trump);
  }
  if (winners.length) {
    if (isLast) return lowest(winners, trump);
    // Not last: win with the lowest card that still wins, but if following suit
    // with the ace (boss of a plain suit) just play it.
    const ace = winners.find((c) => rankOf(c) === 'A' && !isTrump(c, trump));
    if (ace) return ace;
    return lowest(winners, trump);
  }
  return throwOff(legal, hand, trump);
}

export function heuristicDiscard(hand: readonly Card[], trump: Suit): Card {
  const plain = hand.filter((c) => !isTrump(c, trump));
  if (plain.length === 0) return lowest(hand, trump);
  // Void a suit if possible (singleton non-ace), otherwise lowest plain card.
  const singletons = plain.filter(
    (c) => rankOf(c) !== 'A' && plain.filter((x) => printedSuitOf(x) === printedSuitOf(c)).length === 1,
  );
  if (singletons.length) return lowest(singletons, trump);
  const nonAces = plain.filter((c) => rankOf(c) !== 'A');
  return lowest(nonAces.length ? nonAces : plain, trump);
}

function playOne(sim: Sim, card: Card): void {
  const seat = sim.turn;
  const hand = sim.hands[seat]!;
  const idx = hand.indexOf(card);
  if (idx < 0) throw new Error(`sim: ${card} not in hand of seat ${seat}`);
  hand.splice(idx, 1);
  sim.currentTrick.push({ seat, card });
  if (sim.currentTrick.length < activeCount(sim)) {
    sim.turn = nextActive(sim, seat);
    return;
  }
  const w = currentWinner(sim.currentTrick, sim.trump)!;
  sim.tricksWon[teamOf(w)] += 1;
  sim.tricksPlayed += 1;
  sim.currentTrick = [];
  sim.leader = w;
  sim.turn = w;
}

function playout(sim: Sim): void {
  const makerTeam = teamOf(sim.maker);
  while (sim.tricksPlayed < 5) {
    const seat = sim.turn;
    const partnerActive = !sim.sittingOut.includes(partnerOf(seat));
    const card = heuristicPlay(sim.hands[seat]!, sim.currentTrick, sim.trump, seat, makerTeam, partnerActive, activeCount(sim));
    playOne(sim, card);
  }
}

/** Points from `team`'s perspective (+ for us, − for them) plus a tiny trick tiebreak. */
function valueFor(sim: Sim, team: Team, loneDefenderEuchrePoints: number): number {
  const makerTeam = teamOf(sim.maker);
  const mt = sim.tricksWon[makerTeam];
  let pts: number;
  let scoring: Team;
  if (mt === 5) {
    pts = sim.makerAlone ? 4 : 2;
    scoring = makerTeam;
  } else if (mt >= 3) {
    pts = 1;
    scoring = makerTeam;
  } else {
    pts = sim.defenderAlone !== null ? loneDefenderEuchrePoints : 2;
    scoring = (1 - makerTeam) as Team;
  }
  return (scoring === team ? pts : -pts) + 0.01 * sim.tricksWon[team];
}

function cloneSim(sim: Sim): Sim {
  return {
    ...sim,
    hands: sim.hands.map((h) => [...h]),
    sittingOut: [...sim.sittingOut],
    currentTrick: [...sim.currentTrick],
    tricksWon: [...sim.tricksWon] as [number, number],
  };
}

// ---------------------------------------------------------------------------
// Sampling unseen cards consistently with what the bot has observed
// ---------------------------------------------------------------------------

interface Knowledge {
  me: Seat;
  pool: Card[];
  need: number[]; // cards each seat still needs (0 for me / sitting out)
  voids: Set<Suit>[];
  upCardToDealer: boolean;
}

function playedCards(view: PlayerView): Card[] {
  return [...view.tricks.flatMap((t) => t.cards.map((p) => p.card)), ...view.currentTrick.map((p) => p.card)];
}

function observedVoids(view: PlayerView): Set<Suit>[] {
  const voids = [new Set<Suit>(), new Set<Suit>(), new Set<Suit>(), new Set<Suit>()];
  const trump = view.trump;
  if (!trump) return voids;
  const tricks = [...view.tricks.map((t) => t.cards), view.currentTrick];
  for (const cards of tricks) {
    const first = cards[0];
    if (!first) continue;
    const led = effectiveSuit(first.card, trump);
    for (const p of cards.slice(1)) {
      if (effectiveSuit(p.card, trump) !== led) voids[p.seat]!.add(led);
    }
  }
  return voids;
}

function buildKnowledge(view: PlayerView): Knowledge {
  const me = view.seat!;
  const known = new Set<Card>([...view.myHand, ...playedCards(view), ...view.myBuriedCards]);
  const voids = observedVoids(view);
  const need = [0, 0, 0, 0];
  for (const s of [0, 1, 2, 3] as Seat[]) {
    if (s === me || view.sittingOut.includes(s)) continue;
    need[s] = view.handCounts[s];
  }
  let upCardToDealer = false;
  const upKnownElsewhere = known.has(view.upCard);
  if (!upKnownElsewhere && view.upCardStatus === 'picked_up' && me !== view.dealer) {
    const dealerVoid = view.trump ? voids[view.dealer]!.has(effectiveSuit(view.upCard, view.trump)) : false;
    if (!dealerVoid && need[view.dealer]! > 0) {
      upCardToDealer = true;
      need[view.dealer]! -= 1;
    }
  }
  known.add(view.upCard);
  const pool = newDeck().filter((c) => !known.has(c));
  return { me, pool, need, voids, upCardToDealer };
}

/** Returns hands for all four seats, or null if no consistent deal was found. */
function sampleHands(view: PlayerView, k: Knowledge, rng: Rng, trump: Suit | null): Card[][] {
  const holders = [0, 1, 2, 3].filter((s) => k.need[s]! > 0);
  const totalNeed = holders.reduce((a, s) => a + k.need[s]!, 0);
  const deadCap = Math.max(0, k.pool.length - totalNeed);

  for (let attempt = 0; attempt < 30; attempt++) {
    const useVoids = attempt < 25 && trump !== null;
    const cap = [...k.need];
    let dead = deadCap;
    const hands: Card[][] = [[], [], [], []];
    const cards = rng.shuffle([...k.pool]);
    const eligible = (c: Card) =>
      holders.filter((s) => !(useVoids && k.voids[s]!.has(effectiveSuit(c, trump!))));
    cards.sort((a, b) => eligible(a).length - eligible(b).length);
    let ok = true;
    for (const c of cards) {
      const opts = eligible(c).filter((s) => cap[s]! > 0);
      const weight = opts.reduce((a, s) => a + cap[s]!, 0) + dead;
      if (weight === 0) {
        ok = false;
        break;
      }
      let r = rng.int(weight);
      let placed = false;
      for (const s of opts) {
        if (r < cap[s]!) {
          hands[s]!.push(c);
          cap[s]! -= 1;
          placed = true;
          break;
        }
        r -= cap[s]!;
      }
      if (!placed) dead -= 1;
    }
    if (ok && holders.every((s) => cap[s] === 0)) {
      hands[k.me] = [...view.myHand];
      if (k.upCardToDealer) hands[view.dealer]!.push(view.upCard);
      return hands;
    }
  }
  // Should be unreachable; fall back to an unconstrained deal.
  const cards = rng.shuffle([...k.pool]);
  const hands: Card[][] = [[], [], [], []];
  for (const s of holders) hands[s] = cards.splice(0, k.need[s]!);
  hands[k.me] = [...view.myHand];
  if (k.upCardToDealer) hands[view.dealer]!.push(view.upCard);
  return hands;
}

// ---------------------------------------------------------------------------
// Decisions
// ---------------------------------------------------------------------------

interface CallScenario {
  trump: Suit;
  maker: Seat;
  makerAlone: boolean;
  defenderAlone: Seat | null;
  /** Round-1 order up: the dealer adds the up card and discards. */
  pickup: boolean;
  /** If the bot itself is the dealer and wants a specific discard. */
  myDiscard?: Card;
}

function scenarioSim(view: PlayerView, hands: Card[][], sc: CallScenario): Sim {
  const sittingOut: Seat[] = [];
  if (sc.makerAlone) sittingOut.push(partnerOf(sc.maker));
  if (sc.defenderAlone !== null) sittingOut.push(partnerOf(sc.defenderAlone));
  const h = hands.map((x) => [...x]);
  const dealer = view.dealer;
  if (sc.pickup && !sittingOut.includes(dealer)) {
    const dh = h[dealer]!;
    dh.push(view.upCard);
    const discard = dealer === view.seat && sc.myDiscard ? sc.myDiscard : heuristicDiscard(dh, sc.trump);
    dh.splice(dh.indexOf(discard), 1);
  }
  for (const s of sittingOut) h[s] = [];
  let leader = leftOf(dealer);
  while (sittingOut.includes(leader)) leader = leftOf(leader);
  return {
    hands: h,
    trump: sc.trump,
    dealer,
    maker: sc.maker,
    makerAlone: sc.makerAlone,
    defenderAlone: sc.defenderAlone,
    sittingOut,
    currentTrick: [],
    leader,
    turn: leader,
    tricksWon: [0, 0],
    tricksPlayed: 0,
  };
}

/** Average value for my team of a calling scenario, over pre-sampled deals. */
function evalScenario(view: PlayerView, deals: Card[][][], sc: CallScenario): number {
  const team = teamOf(view.seat!);
  let total = 0;
  for (const hands of deals) {
    const sim = scenarioSim(view, hands, sc);
    playout(sim);
    total += valueFor(sim, team, view.rules.loneDefenderEuchrePoints);
  }
  return total / deals.length;
}

function sampleDeals(view: PlayerView, rng: Rng, n: number, trump: Suit | null): Card[][][] {
  const k = buildKnowledge(view);
  const out: Card[][][] = [];
  for (let i = 0; i < n; i++) out.push(sampleHands(view, k, rng, trump));
  return out;
}

const ORDER_THRESHOLD = 0.3;
const ALONE_MARGIN = 0.15;

function decideBid(view: PlayerView, rng: Rng, samples: number, orderThreshold: number, aloneMargin: number): GameAction {
  const me = view.seat!;
  const legal = view.legalActions;
  const deals = sampleDeals(view, rng, samples, null);
  const round1 = view.phase === 'bidding1';
  const canPass = legal.some((a) => a.type === 'pass');
  const isDealerLast = me === view.dealer && !round1;
  const threshold = isDealerLast ? 0.05 : orderThreshold;

  let best: { action: GameAction; ev: number } | null = null;
  const suits: Suit[] = round1 ? [printedSuitOf(view.upCard)] : SUITS.filter((s) => s !== printedSuitOf(view.upCard));
  for (const trump of suits) {
    for (const alone of [false, true]) {
      let ev: number;
      if (round1 && me === view.dealer && !alone) {
        // As dealer, choose the best discard for the evaluation too.
        ev = Math.max(
          ...[...view.myHand, view.upCard].map((d) =>
            evalScenario(view, deals, { trump, maker: me, makerAlone: false, defenderAlone: null, pickup: true, myDiscard: d }),
          ),
        );
      } else {
        ev = evalScenario(view, deals, { trump, maker: me, makerAlone: alone, defenderAlone: null, pickup: round1 });
      }
      if (alone) ev -= aloneMargin; // demand a clear edge before going alone
      const action: GameAction = round1 ? { type: 'order_up', alone } : { type: 'call_trump', suit: trump, alone };
      if (!best || ev > best.ev) best = { action, ev };
    }
  }
  if (best && (best.ev > threshold || !canPass)) return best.action;
  return { type: 'pass' };
}

function decideDefendAlone(view: PlayerView, rng: Rng, samples: number): GameAction {
  const deals = sampleDeals(view, rng, samples, view.trump);
  const base: CallScenario = {
    trump: view.trump!,
    maker: view.maker!,
    makerAlone: view.makerAlone,
    defenderAlone: null,
    pickup: view.callRound === 1,
  };
  const together = evalScenario(view, deals, base);
  const alone = evalScenario(view, deals, { ...base, defenderAlone: view.seat! });
  return { type: 'defend_alone', alone: alone > together + 0.4 };
}

function decideDiscard(view: PlayerView, rng: Rng, samples: number): GameAction {
  const trump = view.trump!;
  const deals = sampleDeals(view, rng, Math.max(20, Math.floor(samples / 2)), trump);
  let best: { card: Card; ev: number } | null = null;
  for (const card of view.myHand) {
    let total = 0;
    for (const hands of deals) {
      const h = hands.map((x) => [...x]);
      h[view.seat!] = view.myHand.filter((c) => c !== card);
      const sim = scenarioSim(view, h, {
        trump,
        maker: view.maker!,
        makerAlone: view.makerAlone,
        defenderAlone: view.defenderAlone,
        pickup: false,
      });
      playout(sim);
      total += valueFor(sim, teamOf(view.seat!), view.rules.loneDefenderEuchrePoints);
    }
    const ev = total / deals.length;
    if (!best || ev > best.ev + 1e-9) best = { card, ev };
  }
  return { type: 'discard', card: best ? best.card : heuristicDiscard(view.myHand, trump) };
}

function decidePlay(view: PlayerView, rng: Rng, samples: number): GameAction {
  const me = view.seat!;
  const trump = view.trump!;
  const legal = view.legalActions.filter((a): a is { type: 'play'; card: Card } => a.type === 'play');
  if (legal.length === 1) return legal[0]!;
  const makerTeam = teamOf(view.maker!);
  const partnerActive = !view.sittingOut.includes(partnerOf(me));
  const active = 4 - view.sittingOut.length;
  const fallback = heuristicPlay(view.myHand, view.currentTrick, trump, me, makerTeam, partnerActive, active);

  const k = buildKnowledge(view);
  const totals = new Map<Card, number>(legal.map((a) => [a.card, 0]));
  for (let i = 0; i < samples; i++) {
    const hands = sampleHands(view, k, rng, trump);
    for (const a of legal) {
      const sim: Sim = {
        hands: hands.map((h) => [...h]),
        trump,
        dealer: view.dealer,
        maker: view.maker!,
        makerAlone: view.makerAlone,
        defenderAlone: view.defenderAlone,
        sittingOut: [...view.sittingOut],
        currentTrick: [...view.currentTrick],
        leader: view.leader!,
        turn: me,
        tricksWon: [...view.tricksWon] as [number, number],
        tricksPlayed: view.tricks.length,
      };
      for (const s of view.sittingOut) sim.hands[s] = [];
      playOne(sim, a.card);
      playout(sim);
      totals.set(a.card, totals.get(a.card)! + valueFor(sim, teamOf(me), view.rules.loneDefenderEuchrePoints));
    }
  }
  let bestCard = fallback;
  let bestEv = totals.get(fallback) ?? -Infinity;
  for (const [card, total] of totals) {
    if (total > bestEv + 1e-9) {
      bestEv = total;
      bestCard = card;
    }
  }
  return { type: 'play', card: bestCard };
}

function decideDealOption(view: PlayerView): GameAction {
  const legal = view.legalActions;
  if (legal.some((a) => a.type === 'redeal')) return { type: 'redeal' };
  const swaps = legal.filter((a): a is { type: 'farmer_swap'; cards: Card[] } => a.type === 'farmer_swap');
  if (swaps.length) {
    // Throw away the nines/tens least likely to become trump (avoid the up-card suit), nines first.
    const upSuit = printedSuitOf(view.upCard);
    const lows = lowCards(view.myHand).sort(
      (a, b) =>
        Number(printedSuitOf(a) === upSuit) - Number(printedSuitOf(b) === upSuit) ||
        cardPower(a, null) - cardPower(b, null),
    );
    const pick = lows.slice(0, 3).sort().join(',');
    return swaps.find((a) => [...a.cards].sort().join(',') === pick) ?? swaps[0]!;
  }
  return { type: 'keep_hand' };
}

/**
 * Choose an action for the seat this view belongs to. Always returns one of
 * `view.legalActions`.
 */
export function chooseBotAction(view: PlayerView, opts: BotOptions = {}): GameAction {
  if (view.seat === null) throw new Error('Spectators cannot act.');
  if (view.legalActions.length === 0) throw new Error('No legal actions for this seat.');
  const rng = new Rng(opts.seed ?? `${view.version}:${view.seat}:${view.dealNumber}`);
  const samples = Math.max(8, opts.samples ?? DEFAULT_SAMPLES);
  let action: GameAction;
  switch (view.phase) {
    case 'deal_options':
      action = decideDealOption(view);
      break;
    case 'bidding1':
    case 'bidding2':
      action = decideBid(view, rng, samples, opts.orderThreshold ?? ORDER_THRESHOLD, opts.aloneMargin ?? ALONE_MARGIN);
      break;
    case 'defend_alone':
      action = decideDefendAlone(view, rng, samples);
      break;
    case 'discard':
      action = decideDiscard(view, rng, samples);
      break;
    case 'playing':
      action = decidePlay(view, rng, samples);
      break;
    default:
      action = view.legalActions[0]!;
  }
  return action;
}
